import { connect } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import i18next from 'i18next';
import PropTypes from 'prop-types';

import { closeGenericModal, openGenericModal, refreshGenericModal } from '@actions/modal';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

import { getClientInfo } from '@selectors/client';

import { ingredient as ingredientService } from '@services/ingredient';

import {
  fetchAllergensList,
  fetchIngredientCategories,
} from '@admin/products/ingredients/detail/components/IngredientInfo/common/services';

import { getQuickCreateIngredientModalConfig } from './utils/getQuickCreateIngredientModalConfig';

const QuickCreateIngredientModal = (props) => {
  const {
    client: { clientId },
    modal,
    initialAllergens,
    onIngredientCreated,
    onClose,
    openGenericModal,
    refreshGenericModal,
    closeGenericModal,
    showSuccessMessage,
    showErrorMessage,
  } = props;

  const [ingredient, setIngredient] = useState({
    name: '',
    category: '',
    categoryId: null,
    unit: '',
    allergens: initialAllergens,
    isStrategic: false,
    isEdible: true,
  });
  const [ingredientsCategories, setIngredientsCategories] = useState([]);
  const [availableAllergens, setAvailableAllergens] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const isClosedRef = useRef(false);
  const isSavingRef = useRef(false);

  useEffect(
    () => () => {
      isClosedRef.current = true;
    },
    [],
  );

  useEffect(() => {
    (async function loadData() {
      try {
        await Promise.all([
          fetchIngredientCategories(setIngredientsCategories),
          fetchAllergensList(setAvailableAllergens),
        ]);
      } catch {
        showErrorMessage(i18next.t('ADMIN.INGREDIENTS.ERROR_FETCH_INGREDIENT_DETAIL'));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const params = getQuickCreateIngredientModalConfig({
      ingredient,
      setIngredient,
      ingredientsCategories,
      availableAllergens,
      isLoading,
      isSaving,
      handleSave,
      handleCloseCleanUp: onClose,
    });

    if (modal.GenericModalBool) {
      refreshGenericModal(params);
      return;
    }

    openGenericModal(params);
  }, [ingredient, ingredientsCategories, availableAllergens, isLoading, isSaving]);

  const handleSave = async () => {
    if (isSavingRef.current) {
      return;
    }

    const { name, unit, categoryId, allergens, isStrategic, isEdible } = ingredient;

    isSavingRef.current = true;
    setIsSaving(true);

    try {
      const createdIngredient = await ingredientService.createIngredient(clientId, {
        name: name.trim(),
        unit,
        categoryId,
        cost: null,
        supplierProducts: [],
        allergenIds: allergens.map(({ id }) => id),
        isStrategic,
        isEdible,
      });

      showSuccessMessage(i18next.t('ADMIN.INGREDIENTS.CREATION_SUCCESS'));

      if (isClosedRef.current) {
        return;
      }

      onIngredientCreated(createdIngredient);
      onClose();
      closeGenericModal();
    } catch {
      showErrorMessage(i18next.t('ADMIN.INGREDIENTS.CREATION_ERROR'));

      if (isClosedRef.current) {
        return;
      }

      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  return null;
};

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
  modal: state.modalReducer,
});

const mapDispatchToProps = (dispatch) => ({
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshGenericModal: (params) => {
    dispatch(refreshGenericModal(params));
  },
  closeGenericModal: () => {
    dispatch(closeGenericModal());
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
});

QuickCreateIngredientModal.propTypes = {
  initialAllergens: PropTypes.array,
  onIngredientCreated: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

QuickCreateIngredientModal.defaultProps = {
  initialAllergens: [],
};

export default connect(mapStateToProps, mapDispatchToProps)(QuickCreateIngredientModal);
